import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { Box, CircularProgress, Typography, Button, Stack } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BlockIcon from "@mui/icons-material/Block";

const REQUIRED_ROLE = "admin";
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "kira-admin";

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function hasRequiredRole(accessToken: string | undefined): boolean {
  if (!accessToken) return false;
  const payload = parseJwtPayload(accessToken);
  if (!payload) return false;
  const resourceAccess = payload.resource_access as
    | Record<string, { roles?: string[] }>
    | undefined;
  const roles = resourceAccess?.[KEYCLOAK_CLIENT_ID]?.roles ?? [];
  return roles.includes(REQUIRED_ROLE);
}

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  const isAdmin = useMemo(
    () => hasRequiredRole(auth.user?.access_token),
    [auth.user?.access_token],
  );

  if (auth.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} />
          <Typography variant="body2">Authenticating...</Typography>
        </Stack>
      </Box>
    );
  }

  if (auth.error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Stack alignItems="center" spacing={2}>
          <LockOutlinedIcon sx={{ fontSize: 48, color: "error.main" }} />
          <Typography variant="h6">Authentication Error</Typography>
          <Typography variant="body2" color="text.secondary">
            {auth.error.message}
          </Typography>
          <Button variant="contained" onClick={() => auth.signinRedirect()}>
            Try Again
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Stack alignItems="center" spacing={3} sx={{ textAlign: "center", maxWidth: 400 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h4" fontWeight={800} color="white">
              K
            </Typography>
          </Box>
          <Typography variant="h5">Kira Admin</Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to manage Kira, your AI engineering assistant.
          </Typography>
          <Button variant="contained" size="large" onClick={() => auth.signinRedirect()} sx={{ px: 5 }}>
            Sign In with Keycloak
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Stack alignItems="center" spacing={2} sx={{ textAlign: "center", maxWidth: 440 }}>
          <BlockIcon sx={{ fontSize: 48, color: "error.main" }} />
          <Typography variant="h6">Access Denied</Typography>
          <Typography variant="body2" color="text.secondary">
            Your account does not have the required role to access Kira.
            Contact your administrator to request access.
          </Typography>
          <Button variant="outlined" onClick={() => auth.signoutRedirect()}>
            Sign Out
          </Button>
        </Stack>
      </Box>
    );
  }

  return <>{children}</>;
}
